import { useEffect, useRef, useCallback, useState } from "react";

// Singleton audio instance — survives re-renders, only one plays at a time
let audioInstance = null;

function getAudio() {
  if (!audioInstance) {
    audioInstance = new Audio("/sounds/notification.wav");
    audioInstance.loop = true;
    audioInstance.preload = "auto";
  }
  return audioInstance;
}

export function startNewOrderSound() {
  const audio = getAudio();
  audio.currentTime = 0;

  return audio.play().catch((error) => {
    console.error("[Audio] lecture bloquee par le navigateur:", error);
    return false;
  });
}

export function unlockNewOrderSound() {
  const audio = getAudio();
  const previousVolume = audio.volume;
  audio.volume = 0;

  return audio.play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = previousVolume;
      console.log("[Audio] notifications sonores activees");
      return true;
    })
    .catch((error) => {
      audio.volume = previousVolume;
      console.warn("[Audio] activation impossible:", error);
      return false;
    });
}

export function stopNewOrderSound() {
  const audio = getAudio();
  audio.pause();
  audio.currentTime = 0;
}

/**
 * Detects new status_id=1 orders across polls and plays a looping alert sound.
 * Returns { newOrderIds, acknowledge } — call acknowledge() to stop the sound
 * and clear the badge.
 */
export function useNewOrderAlert(orders) {
  const knownIdsRef = useRef(null); // null = first load (don't alert on mount)
  const [newOrderIds, setNewOrderIds] = useState([]);

  const stopSound = useCallback(() => {
    stopNewOrderSound();
  }, []);

  const startSound = useCallback(() => {
    // Browsers block autoplay until the user has interacted with the page.
    // play() returns a promise; silence the rejection to avoid console noise.
    startNewOrderSound();
  }, []);

  useEffect(() => {
    if (!orders) return;

    const currentNewIds = orders
      .filter((o) => o.status_id === 1)
      .map((o) => o.id);

    // First load — just record the baseline, don't alert
    if (knownIdsRef.current === null) {
      knownIdsRef.current = new Set(currentNewIds);
      return;
    }

    const brandNew = currentNewIds.filter(
      (id) => !knownIdsRef.current.has(id)
    );

    if (brandNew.length > 0) {
      knownIdsRef.current = new Set(currentNewIds);
      setNewOrderIds((prev) => [...new Set([...prev, ...brandNew])]);
      startSound();
    } else {
      // Update known set (orders may have moved out of status 1)
      knownIdsRef.current = new Set(currentNewIds);
    }
  }, [orders, startSound]);

  const acknowledge = useCallback(() => {
    stopSound();
    setNewOrderIds([]);
  }, [stopSound]);

  // Stop sound if component unmounts
  useEffect(() => () => stopSound(), [stopSound]);

  return { newOrderIds, acknowledge };
}
